import { Head, Link, router, useForm } from '@inertiajs/react'
import { ArrowLeft, KeyRound, Power, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import {
  disableUserPath,
  enableUserPath,
  resetPasswordUserPath,
  userPath,
  usersPath,
} from '@/lib/routes'
import type { UserRole } from '@/types/inertia'

type UserStatus = 'active' | 'pending' | 'disabled'

type EditUser = {
  id: number
  email: string
  first_name: string
  last_name: string
  full_name: string
  role: UserRole
  status: UserStatus
  invitation_pending: boolean
}

type UsersEditProps = {
  user: EditUser
}

type EditForm = {
  first_name: string
  last_name: string
  email: string
  role: UserRole
}

const STATUS_VARIANT: Record<UserStatus, 'default' | 'accent' | 'destructive'> = {
  active: 'default',
  pending: 'accent',
  disabled: 'destructive',
}

const STATUS_LABEL: Record<UserStatus, string> = {
  active: 'Active',
  pending: 'Pending invite',
  disabled: 'Disabled',
}

function UsersEdit({ user }: UsersEditProps) {
  const form = useForm<EditForm>({
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    role: user.role,
  })

  const fieldError = (key: keyof EditForm) =>
    (form.errors as Record<string, string>)[key]

  return (
    <>
      <Head title={`${user.full_name || user.email} — Users`} />

      <div className="mx-auto max-w-2xl px-8 py-10">
        <Link
          href={usersPath()}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to users
        </Link>

        <PageHeader
          title={user.full_name || user.email}
          description={user.email}
          actions={<Badge variant={STATUS_VARIANT[user.status]}>{STATUS_LABEL[user.status]}</Badge>}
        />

        <form
          onSubmit={(event) => {
            event.preventDefault()
            form.transform((data) => ({ user: data }))
            form.patch(userPath(user.id))
          }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Profile</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="first_name">First name</Label>
                <Input
                  id="first_name"
                  value={form.data.first_name}
                  onChange={(e) => form.setData('first_name', e.target.value)}
                />
                {fieldError('first_name') && (
                  <p className="text-xs text-destructive">{fieldError('first_name')}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="last_name">Last name</Label>
                <Input
                  id="last_name"
                  value={form.data.last_name}
                  onChange={(e) => form.setData('last_name', e.target.value)}
                />
                {fieldError('last_name') && (
                  <p className="text-xs text-destructive">{fieldError('last_name')}</p>
                )}
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={form.data.email}
                  onChange={(e) => form.setData('email', e.target.value)}
                />
                {fieldError('email') && (
                  <p className="text-xs text-destructive">{fieldError('email')}</p>
                )}
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  id="role"
                  value={form.data.role}
                  onChange={(e) => form.setData('role', e.target.value as UserRole)}
                >
                  <option value="partner">Partner</option>
                  <option value="lp">Limited partner</option>
                  <option value="founder">Founder</option>
                </Select>
                {fieldError('role') && (
                  <p className="text-xs text-destructive">{fieldError('role')}</p>
                )}
              </div>
            </CardContent>
            <CardFooter className="justify-end gap-2 border-t border-border pt-4">
              <Link href={usersPath()}>
                <Button type="button" variant="ghost">
                  Cancel
                </Button>
              </Link>
              <Button type="submit" disabled={form.processing}>
                {form.processing ? 'Saving…' : 'Save changes'}
              </Button>
            </CardFooter>
          </Card>
        </form>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Account actions</CardTitle>
            <CardDescription>
              Password reset, disable/enable access, or permanently delete the user.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between gap-4 rounded-md border border-border p-4">
              <div>
                <p className="text-sm font-medium">
                  {user.invitation_pending ? 'Resend invitation' : 'Send password reset'}
                </p>
                <p className="text-sm text-muted-foreground">
                  Emails {user.email} a secure link to set a new password.
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={() => router.post(resetPasswordUserPath(user.id))}
              >
                <KeyRound />
                {user.invitation_pending ? 'Resend' : 'Send reset'}
              </Button>
            </div>

            {user.status === 'disabled' ? (
              <div className="flex items-center justify-between gap-4 rounded-md border border-border p-4">
                <div>
                  <p className="text-sm font-medium">Enable account</p>
                  <p className="text-sm text-muted-foreground">
                    Restore access. The user will be able to sign in again.
                  </p>
                </div>
                <Button variant="secondary" onClick={() => router.post(enableUserPath(user.id))}>
                  <Power />
                  Enable
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-4 rounded-md border border-border p-4">
                <div>
                  <p className="text-sm font-medium">Disable account</p>
                  <p className="text-sm text-muted-foreground">
                    Revoke sign-in access. The user is preserved and can be re-enabled later.
                  </p>
                </div>
                <Button variant="secondary" onClick={() => router.post(disableUserPath(user.id))}>
                  <Power />
                  Disable
                </Button>
              </div>
            )}

            <div className="flex items-center justify-between gap-4 rounded-md border border-destructive/40 p-4">
              <div>
                <p className="text-sm font-medium text-destructive">Delete user</p>
                <p className="text-sm text-muted-foreground">
                  Permanently removes the account and its memberships. This can&apos;t be undone.
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={() => {
                  if (window.confirm(`Delete ${user.email}? This can't be undone.`)) {
                    router.delete(userPath(user.id))
                  }
                }}
              >
                <Trash2 />
                Delete
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

export default Object.assign(UsersEdit, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
